import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useStorage } from '@vueuse/core';
import { createFetch } from '@vueuse/core';
import KeycloakService, { KeycloakUser } from 'src/security/KeycloakService';
import { serverApiBaseUrl } from 'src/services/common';

type TokenType = string | null;

const toBooleanFlag = (value: unknown) =>
  ['true', '1', 'yes', 'on'].includes(
    String(value ?? '')
      .trim()
      .toLowerCase(),
  );

const DEV_AUTH_MOCK_ENABLED =
  import.meta.env.DEV && toBooleanFlag(import.meta.env.VITE_DEV_AUTH_MOCK);

const DEV_AUTH_TOKEN = 'local-dev-mock-token';

const DEV_AUTH_USER: KeycloakUser = {
  id: 'dev-user',
  username: 'dev.user',
  email: 'dev.user@example.com',
  firstname: 'Dev',
  lastname: 'User',
  fullname: 'Dev User',
  roles: ['developer', 'admin'],
};

const ROLE_ALIASES = {
  admin: ['admin', 'role_admin'],
  manager: ['manager', 'role_manager'],
  cashbox: ['cashbox', 'cashboxuser', 'role_cashbox', 'role_cashboxuser'],
} satisfies Record<string, readonly string[]>;

type ToggleableRole = keyof typeof ROLE_ALIASES;
const ROLE_HIERARCHY: ToggleableRole[] = ['manager', 'cashbox', 'admin'];

const getRoleAliases = (role: ToggleableRole): readonly string[] => ROLE_ALIASES[role];

const normalizeRoles = (roles: unknown) =>
  Array.isArray(roles)
    ? roles.map((role) => String(role).trim().toLowerCase()).filter(Boolean)
    : [];

export const useAuthStore = defineStore('auth', () => {
  const token = useStorage<TokenType>('member-auth-token', null);
  const user = ref<KeycloakUser | null>(null);
  const realRoles = ref<string[]>([]);
  const activeRole = ref<ToggleableRole | null>(null);
  const isReady = ref(false);

  const isAuthenticated = computed(() => Boolean(token.value));

  const logUserRoles = (context: string) => {
    const roles = user.value?.roles ?? [];
    console.log(`[AuthStore] ${context} user roles:`, roles);
  };

  if (DEV_AUTH_MOCK_ENABLED) {
    token.value = DEV_AUTH_TOKEN;
    user.value = { ...DEV_AUTH_USER };
    realRoles.value = normalizeRoles(DEV_AUTH_USER.roles);
    activeRole.value = 'admin';
    isReady.value = true;
    logUserRoles('Mock init');
  }

  const getRoleIndex = (role: ToggleableRole | null) =>
    role ? ROLE_HIERARCHY.indexOf(role) : -1;

  const highestRealRole = () =>
    [...ROLE_HIERARCHY]
      .reverse()
      .find((role) => getRoleAliases(role).some((alias) => realRoles.value.includes(alias))) ?? null;

  const hasRealRole = (role: ToggleableRole) =>
    getRoleIndex(highestRealRole()) >= getRoleIndex(role);

  const rebuildEffectiveUser = (baseUser: KeycloakUser | null) => {
    if (!baseUser) {
      user.value = null;
      return;
    }

    const baseRoles = normalizeRoles(baseUser.roles);
    const passiveRoles = baseRoles.filter(
      (role) =>
        !ROLE_HIERARCHY.some((toggleableRole) =>
          getRoleAliases(toggleableRole).includes(role),
        ),
    );
    const activeRoleIndex = getRoleIndex(activeRole.value);
    const effectiveToggleableRoles =
      activeRoleIndex >= 0
        ? ROLE_HIERARCHY.slice(0, activeRoleIndex + 1).flatMap((role) =>
            getRoleAliases(role),
          )
        : [];
    const nextRoles = Array.from(new Set([...passiveRoles, ...effectiveToggleableRoles]));

    user.value = {
      ...baseUser,
      roles: nextRoles,
    };
  };

  const applyAuthenticatedUser = (baseUser: KeycloakUser | null) => {
    realRoles.value = normalizeRoles(baseUser?.roles);
    const highestRole = highestRealRole();
    if (highestRole === null) {
      activeRole.value = null;
    } else if (activeRole.value === null || getRoleIndex(activeRole.value) > getRoleIndex(highestRole)) {
      activeRole.value = highestRole;
    }
    rebuildEffectiveUser(baseUser);
  };

  const syncSessionFromKeycloak = () => {
    token.value = KeycloakService.getToken();
    applyAuthenticatedUser(KeycloakService.getUserFromToken());
    logUserRoles('Keycloak sync');
  };

  const initialize = async () => {
    if (DEV_AUTH_MOCK_ENABLED) {
      token.value = DEV_AUTH_TOKEN;
      applyAuthenticatedUser({ ...DEV_AUTH_USER });
      isReady.value = true;
      logUserRoles('Mock initialize');
      return true;
    }

    try {
      const authenticated = await KeycloakService.init();
      if (!authenticated && KeycloakService.isAuthDisabled) {
        const reason = KeycloakService.disableReason;
        if (reason) {
          console.warn('[AuthStore] Authentication disabled:', reason);
        }
      }
      if (authenticated) {
        syncSessionFromKeycloak();
        console.info('[AuthStore] Authenticated user roles:', {
          roles: user.value?.roles ?? [],
          username: user.value?.username,
        });
        return true;
      }

      token.value = null;
      user.value = null;
      realRoles.value = [];
      activeRole.value = null;
      return false;
    } catch (error) {
      console.error('[AuthStore] Keycloak initialization failed', error);
      token.value = null;
      user.value = null;
      realRoles.value = [];
      activeRole.value = null;
      return false;
    } finally {
      isReady.value = true;
    }
  };

  const login = async () => {
    if (DEV_AUTH_MOCK_ENABLED) {
      token.value = DEV_AUTH_TOKEN;
      applyAuthenticatedUser({ ...DEV_AUTH_USER });
      logUserRoles('Mock login');
      return;
    }

    await KeycloakService.login();

    if (KeycloakService.isAuthDisabled) {
      syncSessionFromKeycloak();
      logUserRoles('Auth-disabled login');
    }
  };

  const logout = async () => {
    if (DEV_AUTH_MOCK_ENABLED) {
      token.value = null;
      user.value = null;
      realRoles.value = [];
      activeRole.value = null;
      return;
    }

    await KeycloakService.logout();
    token.value = null;
    user.value = null;
    realRoles.value = [];
    activeRole.value = null;
  };

  const ensureToken = async () => {
    if (DEV_AUTH_MOCK_ENABLED) {
      token.value = DEV_AUTH_TOKEN;
      applyAuthenticatedUser({ ...DEV_AUTH_USER });
      logUserRoles('Mock ensureToken');
      return DEV_AUTH_TOKEN;
    }

    const freshToken = await KeycloakService.ensureToken();
    if (!freshToken) {
      token.value = null;
      user.value = null;
      realRoles.value = [];
      activeRole.value = null;
      return null;
    }

    token.value = freshToken;
    applyAuthenticatedUser(KeycloakService.getUserFromToken());
    logUserRoles('ensureToken');
    return freshToken;
  };

  const hasActiveRole = (role: ToggleableRole) =>
    getRoleAliases(role).some((alias) =>
      normalizeRoles(user.value?.roles).includes(alias),
    );

  const toggleRole = (role: ToggleableRole) => {
    if (!hasRealRole(role)) {
      return;
    }

    const roleIndex = getRoleIndex(role);
    activeRole.value = hasActiveRole(role)
      ? (roleIndex > 0 ? ROLE_HIERARCHY[roleIndex - 1] : null)
      : role;

    rebuildEffectiveUser(user.value ? { ...user.value, roles: realRoles.value } : null);
    logUserRoles(`Toggle ${role}`);
  };

  const getSrvBaseUrl = () => serverApiBaseUrl();

  const useAuthorizedFetch = createFetch({
    baseUrl: serverApiBaseUrl(),
    options: {
      async beforeFetch({ options }) {
        const devApiToken = import.meta.env.VITE_DEV_API_TOKEN?.trim();
        if (import.meta.env.DEV && devApiToken) {
          options.headers = {
            ...options.headers,
            Authorization: `Bearer ${devApiToken}`,
          };
          return { options };
        }

        const freshToken = await ensureToken();
        if (!freshToken) {
          throw new Error('User is not authenticated');
        }

        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${freshToken}`,
        };

        return { options };
      },
      onFetchError({ data, error }) {
        console.error('Fetch Error:', error);
        return { data, error };
      },
    },
    fetchOptions: {
      mode: 'cors',
    },
  });

  return {
    DEV_AUTH_MOCK_ENABLED,
    token,
    user,
    realRoles,
    isReady,
    isAuthenticated,
    initialize,
    login,
    logout,
    ensureToken,
    hasRealRole,
    hasActiveRole,
    toggleRole,
    useAuthorizedFetch,
    getSrvBaseUrl,
  };
});
